import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { ActivityMemoAction, ActivityMemoGetter } from '@/store/activityMemo/activityMemo.module';
import { ActivityMemoResultSchema } from '@/store/activityMemo/activityMemo.validator';
import { ActivityMemo } from '@/store/activityMemo/activityMemo.model';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ActivityMemoResultFormComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  emits: [
    'submited'
  ],
  props: {
    activityId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    },
    activityMemoId: {
      type: Number,
    }
  },
  setup: (props, context) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => activityMemo.value?.result != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขผลการปฏิบัติราชการ' : 'เพิ่มผลการปฏิบัติราชการ');
    const activityMemo = computed<ActivityMemo | undefined>(() => store.getters[ActivityMemoGetter.BY_ID](props.activityMemoId));
    const activityMemoResultForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      activityId?: number | null;
      provinceId?: number | null;
      id?: number | null;
      isDeleteMode: boolean;
      activityDate?: Date | null;
      latitude?: number | null;
      longitude?: number | null;
      description?: string | null;
    }
    const formData = reactive<Form>({
      activityId: null,
      provinceId: null,
      id: null,
      isDeleteMode: false,
      activityDate: null,
      latitude: null,
      longitude: null,
      description: null
    });
    const validator = computed(() => ActivityMemoResultSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      activityDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'activityDate')),
    });
    const isValid = computed(() => !errors.activityDate);
    const isLoading = computed<boolean>(() => store.getters[ActivityMemoGetter.STATUS] === 'init' || store.getters[ActivityMemoGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.activityId = props.activityId;
        formData.provinceId = props.provinceId;
        formData.id = props.activityMemoId;

        if (isEditMode.value) {
          formData.activityDate = activityMemo.value?.result?.activityDate;
          formData.latitude = activityMemo.value?.result?.location?.latitude;
          formData.longitude = activityMemo.value?.result?.location?.longitude;
          formData.description = activityMemo.value?.result?.description;
        }
        resolve();
      })
      .then(() => activityMemoResultForm.value?.show());
    };

    // hide modal
    const hide = () => {
      activityMemoResultForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.activityId = null;
        formData.provinceId = null;
        formData.id = null;
        formData.activityDate = null;
        formData.latitude = null;
        formData.longitude = null;
        formData.description = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        store.dispatch(ActivityMemoAction.UPDATE_RESULT, {
          ...formData,
          location: (formData.latitude && formData.longitude)
            ? { latitude: formData.latitude, longitude: formData.longitude }
            : null
        })
          .then(() => {
            context.emit('submited');
            hide();
          });
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            new Promise<void>((resolve) => {
              formData.isDeleteMode = true;
              formData.latitude = null;
              formData.longitude = null;
              formData.description = '';
              resolve();
            }).then(() => store.dispatch(ActivityMemoAction.UPDATE_RESULT, formData)
                .then(() => {
                  context.emit('submited');
                  hide();
                }));
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      activityMemoResultForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
